import React, { useCallback } from 'react'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useFormik } from 'formik'
import { useDispatch } from 'react-redux'
import { useRequests } from '../requests/useRequests'
import { ContractorReceipt } from '../types/contractor-receipt'
import { validatePositiveNumber } from '../utils/validators'
import { ErrorAlert } from '../formik/ErrorAlert'
import { getFileFromBuffer } from '../browser/utils'
import { RefreshActions } from '../state/refresh'
import { useIsMobileView } from '../hooks/useIsMobileView'

interface GenerateContractorReceiptDialogProps {
  contractorId: number
  handleClose: (b: boolean) => void
}

const GenerateContractorReceiptDialog = ({
  contractorId,
  handleClose
}: GenerateContractorReceiptDialogProps): JSX.Element => {
  const dispatch = useDispatch()
  const isMobileView = useIsMobileView()
  const requestMaker = useRequests()

  const initContractorReceipt = {
    contractorId
  }

  const validate = (
    contractorReceipt: Partial<ContractorReceipt>
  ): Partial<ContractorReceipt> => {
    const errors: Record<string, string> = {}

    validatePositiveNumber(
      contractorReceipt.unitNetto ?? null,
      e => (errors.unitNetto = e)
    )

    validatePositiveNumber(
      contractorReceipt.vatRate ?? null,
      e => (errors.vatRate = e)
    )

    validatePositiveNumber(
      contractorReceipt.quantity ?? null,
      e => (errors.quantity = e)
    )

    return errors
  }

  const onSubmit = useCallback(
    async (contractorReceipt: Partial<ContractorReceipt>): Promise<void> => {
      handleClose(true)
      const receipts = await requestMaker.generateReceipts([contractorReceipt])
      dispatch(RefreshActions.refreshUserState())
      // printFileFromBuffer(receipts.data, 'application/pdf')
      getFileFromBuffer(
        receipts.data,
        'application/pdf',
        isMobileView ? 'receipts.pdf' : undefined
      )
      handleClose(true)
    },
    [requestMaker, dispatch, handleClose, isMobileView]
  )

  const formik = useFormik({
    initialValues: initContractorReceipt,
    onSubmit,
    enableReinitialize: true,
    validate
  })

  return (
    <div>
      <Dialog open onClose={handleClose}>
        <form>
          <DialogTitle>Generate receipt</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin='dense'
              id='receiptNumber'
              label='Receipt number'
              type='name'
              fullWidth
              variant='standard'
              value={formik.values.receiptNumber}
              onChange={formik.handleChange}
            />
            <TextField
              margin='dense'
              id='unitNetto'
              label='Unit netto'
              type='number'
              fullWidth
              variant='standard'
              value={formik.values.unitNetto}
              onChange={formik.handleChange}
            />
            <ErrorAlert formik={formik} name='unitNetto' />
            <TextField
              margin='dense'
              id='vatRate'
              label='Vat rate (%)'
              type='percentage'
              fullWidth
              variant='standard'
              value={formik.values.vatRate}
              onChange={formik.handleChange}
            />
            <ErrorAlert formik={formik} name='vatRate' />
            <TextField
              margin='dense'
              id='quantity'
              label='Quantity'
              type='number'
              fullWidth
              variant='standard'
              value={formik.values.quantity}
              onChange={formik.handleChange}
            />
            <ErrorAlert formik={formik} name='quantity' />
            <TextField
              margin='dense'
              id='service'
              label='Service'
              type='name'
              fullWidth
              variant='standard'
              value={formik.values.service}
              onChange={formik.handleChange}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose as any}>Cancel</Button>
            <Button onClick={formik.handleSubmit as any}>Generate</Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}

export default GenerateContractorReceiptDialog
